import React, { useState, useEffect, useRef } from 'react';
import { Helmet } from 'react-helmet-async';
import { IonButton, IonToast } from '@ionic/react';
import { accountApi as ApiService, ACCOUNT_ENDPOINTS as USER_ENDPOINTS } from 'services/api';
import PageLayout from '../../components/layouts/PageLayout';
import PageHeader from '../../components/atoms/PageHeader/PageHeader';
import TokenCreateModal from '../../components/organisms/Modals/TokenCreateModal/TokenCreateModal';
import TokensList from './TokensList';
import './TokensPage.css';

const TokensPage = () => {
  const [tokens, setTokens] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [createdToken, setCreatedToken] = useState(null);
  const [creating, setCreating] = useState(false);
  const [toast, setToast] = useState({ show: false, message: '', color: '' });
  const generateButtonRef = useRef(null);
  const emptyStateButtonRef = useRef(null);
  
  // Form state
  const [tokenName, setTokenName] = useState('');
  const [expirationDays, setExpirationDays] = useState(30);

  useEffect(() => {
    loadTokens();
  }, []);

  const loadTokens = async () => {
    try {
      setLoading(true);
      const data = await ApiService.get(USER_ENDPOINTS.TOKENS);
      setTokens(data.tokens || []);
    } catch (error) {
      console.error('Failed to load tokens:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateToken = async (e) => {
    e.preventDefault();
    if (!tokenName.trim()) return;

    try {
      setCreating(true);
      const data = await ApiService.post(USER_ENDPOINTS.TOKENS, {
        name: tokenName.trim(),
        expirationDays
      });
      setCreatedToken(data);
      setShowCreateForm(false);
      setTokenName('');
      setExpirationDays(30);
      loadTokens();
    } catch (error) {
      console.error('Failed to create token:', error);
      alert(`Failed to create token: ${error.message || 'Unknown error'}`);
    } finally {
      setCreating(false);
    }
  };

  const handleRevokeToken = async (token) => {
    if (!window.confirm(`Are you sure you want to revoke "${token.name}"? This cannot be undone.`)) {
      return;
    }

    try {
      await ApiService.delete(USER_ENDPOINTS.TOKEN(token.tokenId));
      showToast('Token revoked successfully', 'success');
      loadTokens();
    } catch (error) {
      console.error('Failed to revoke token:', error);
      showToast('Failed to revoke token', 'danger');
    }
  };

  const handleCopyToken = (token) => {
    navigator.clipboard.writeText(token);
    showToast('Token copied to clipboard', 'success');
  };

  const showToast = (message, color) => {
    setToast({ show: true, message, color });
  };

  const handleAddToken = () => {
    setShowCreateForm(!showCreateForm);
  };

  const handleCloseModal = () => {
    setShowCreateForm(false);
    setTokenName('');
    setExpirationDays(30);
  };

  const activeTokens = tokens.filter(t => !t.revoked && !t.expired);

  return (
    <>
      <Helmet>
        <title>Personal Access Tokens - Zibby</title>
      </Helmet>
      <PageLayout
        breadcrumbs={[{ label: 'Personal Access Tokens' }]}
        actions={
          !loading && activeTokens.length > 0 && (
            <IonButton
              ref={generateButtonRef}
              className="header-action-btn"
              color="primary"
              onClick={handleAddToken}
            >
              Generate
            </IonButton>
          )
        }
      >
        <PageHeader subtitle="Use tokens to access Zibby API from CI/CD pipelines and scripts." />

        {/* Success message */}
        {createdToken && (
          <div className="token-card token-success-card">
            <div className="token-info">
              <div className="token-name">
                Copy it now - you won't see it again.
              </div>
              <div className="token-display-inline">
                <code>{createdToken.token}</code>
              </div>
            </div>
            <div className="token-actions">
              <button 
                className="btn-primary btn-sm"
                onClick={() => handleCopyToken(createdToken.token)}
              >
                Copy
              </button>
              <button 
                className="btn-secondary btn-sm"
                onClick={() => setCreatedToken(null)}
              >
                Dismiss
              </button>
            </div>
          </div>
        )}

        {/* Token Create Modal */}
        <TokenCreateModal
          isOpen={showCreateForm}
          onDismiss={handleCloseModal}
          tokenName={tokenName}
          setTokenName={setTokenName}
          expirationDays={expirationDays}
          setExpirationDays={setExpirationDays}
          onSubmit={handleCreateToken}
          isSubmitting={creating}
          triggerRef={activeTokens.length > 0 ? generateButtonRef : emptyStateButtonRef}
        />

        {/* Tokens List */}
        <TokensList
          tokens={activeTokens}
          loading={loading}
          onRevoke={handleRevokeToken}
          onAdd={handleAddToken}
          emptyStateButtonRef={emptyStateButtonRef}
        />

        {/* Toast Notifications */}
        <IonToast
          isOpen={toast.show}
          message={toast.message}
          duration={3000}
          onDidDismiss={() => setToast({ show: false, message: '', color: '' })}
          cssClass="custom-toast"
        />
      </PageLayout>
    </>
  );
};

export default TokensPage;
